import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Inject,
  Page,
  Toolbar,
} from '@syncfusion/ej2-react-grids';
import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { ProductEdit } from './ProductEdit';

import { CloseButton } from '@components/ActionButtons';
import { Box } from '@components/Box';
import { FormButtonsContainer } from '@components/Container';
import { CancelButton } from '@components/Form';
import { CustomSelectOption } from '@components/Form/CustomSelect';
import { OkButton } from '@components/Form/OkButton';
import { DynamicRecordFilter, TableActions } from '@components/Grid';
import { Modal } from '@components/Modal';
import { Space } from '@components/Space';
import { useGridFilters } from '@hooks/useGridFilters';
import { useGridOperations } from '@hooks/useGridOperations';
import { useNotification } from '@hooks/useNotification';
import { useSyncfusionGrid } from '@hooks/useSyncfusionGrid';
import { ProductService } from '@services/Product';

type CategoryContextType = {
  captureRefreshFunction: (refreshFn: () => void) => void;
};

export const ProductList: React.FC = () => {
  const { captureRefreshFunction } = useOutletContext<CategoryContextType>();
  const [showEditProduct, setShowEditProduct] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [productName, setProductName] = useState<string>('');
  const [selectedProductId, setSelectedProductId] = useState<number>(0);
  const [queryFilter, setQueryFilter] = useState<string>('');
  const { showError } = useNotification();

  // Product Type filter options
  const productTypeOptions: CustomSelectOption[] = [
    { value: 'buy', text: 'Buy' },
    { value: 'sell', text: 'Sell' },
  ];

  // Create a service wrapper that matches useSyncfusionGrid interface
  const productServiceWrapper = {
    ...ProductService,
    getList: async (
      page: number,
      take: number | undefined,
      sortBy: string | undefined,
      sortDirection: string | undefined,
      filters: string
    ) => {
      return ProductService.getlist(
        page,
        take,
        undefined,
        undefined,
        filters,
        sortBy,
        sortDirection
      );
    },
  };

  // Custom hooks for grid functionality
  const {
    filters,
    updateFilter,
    updateFilterAndApply,
    clearFilters,
    applyFilters,
  } = useGridFilters({ nameFilter: '', typeFilter: '' }, setQueryFilter);

  const {
    gridData,
    gridRef,
    gridKey,
    renderComplete,
    dataStateChange,
    fetchData,
    editOptions,
    pageSettings,
  } = useSyncfusionGrid({
    service: productServiceWrapper,
    queryFilter,
    defaultSortBy: 'ID',
    defaultSortDirection: 'desc',
  });

  const { handleDelete: handleDeleteOperation } = useGridOperations({
    service: ProductService,
    refreshGrid: fetchData,
    entityName: 'product',
  });

  useEffect(() => {
    captureRefreshFunction(fetchData);
  }, [captureRefreshFunction, fetchData]);

  const handleEdit = (rowId: any) => {
    setSelectedProductId(rowId);
    setShowEditProduct(true);
  };

  const handleDelete = async (rowId: number, rowProductName: string) => {
    try {
      setSelectedProductId(rowId);
      setProductName(rowProductName);
      setShowDelete(true);
    } catch (error) {
      const errorMessage = `Failed to handle delete action for product ${rowProductName}.`;
      console.error(errorMessage);
      showError(errorMessage);
    }
  };

  const confirmDelete = () => {
    handleDeleteOperation(selectedProductId, productName);
    setShowDelete(false);
  };

  const actionButtons = (props: any) => {
    return (
      <TableActions
        dropdownActions={[
          {
            title: 'Edit',
            onClick: () => handleEdit(props.id),
          },
          {
            showTopSeparator: true,
            title: 'Delete',
            onClick: () => handleDelete(props.id, props.name),
          },
        ]}
      />
    );
  };

  return (
    <>
      <DynamicRecordFilter
        label="Filters"
        filters={[
          {
            label: 'Filter by Name',
            type: 'text',
            value: filters.nameFilter || '',
            onChange: (e) => updateFilter('nameFilter', e.target.value),
          },
          {
            label: 'Select Type',
            type: 'select',
            selection: productTypeOptions,
            value: filters.typeFilter || '',
            onChange: (e) => {
              updateFilterAndApply('typeFilter', e.target.value, () =>
                fetchData(true)
              );
            },
          },
        ]}
        onFilter={() => {
          applyFilters(() => fetchData(true));
        }}
        onClear={() => {
          clearFilters();
          applyFilters(() => fetchData(true));
        }}
      />

      <Space />

      <GridComponent
        key={gridKey}
        dataBound={renderComplete}
        dataSource={gridData}
        ref={gridRef}
        allowPaging={true}
        pageSettings={pageSettings}
        editSettings={editOptions}
        dataStateChange={dataStateChange}
      >
        <ColumnsDirective>
          <ColumnDirective field="name" headerText="Name" />
          <ColumnDirective field="price" headerText="Price" />
          <ColumnDirective
            headerText="Actions"
            width={100}
            template={actionButtons}
          />
        </ColumnsDirective>
        <Inject services={[Page, Toolbar]} />
      </GridComponent>

      {showEditProduct && (
        <Modal show={true}>
          {selectedProductId && (
            <ProductEdit
              productId={selectedProductId}
              onClose={(refreshGrid?: boolean) => {
                setShowEditProduct(false);
                if (refreshGrid) {
                  fetchData(true);
                }
              }}
              onDelete={() => {
                setShowEditProduct(false);
                handleDelete(selectedProductId, productName);
              }}
            />
          )}
        </Modal>
      )}

      {showDelete && (
        <Modal show={true}>
          <CloseButton onClick={() => setShowDelete(false)} />

          <Box size="medium">
            <div className="px-4 pb-2">
              <span>Are you sure to delete Product: {productName}</span>
            </div>
            <FormButtonsContainer>
              <div className="space-x-2">
                <CancelButton onClick={() => setShowDelete(false)} />
                <OkButton onClick={confirmDelete}>Delete</OkButton>
              </div>
            </FormButtonsContainer>
          </Box>
        </Modal>
      )}
    </>
  );
};
